/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import { withIsMobile } from 'bundles/directory-graphql/common/renderUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

const DESKTOP_SQUARE_ENTITY_CARD_DIMENSION = 124;
const MOBILE_SQUARE_ENTITY_CARD_DIMENSION = 72;

const styles = {
  cardContainer: css`
    max-width: 95%;
    margin: 20px 0;
    padding: 20px 18px;
    border: 1px solid #929599;
    border-radius: 4px;

    &:hover {
      box-shadow: 0 2px 4px rgb(0 0 0 / 15%);
    }
    ${breakpoints.down('xs')} {
      padding: 15px 20px;
    }
  `,
  imageWrapper: css`
    height: 0;
    overflow: hidden;
    padding-bottom: 100%;
    width: 124px;
    ${breakpoints.down('xs')} {
      width: 72px;
    }
  `,
  imageContainer: css`
    margin-right: 20px;
    max-width: 124px;
    ${breakpoints.down('xs')} {
      max-width: 72px;
      margin-right: 15px;
    }
    /* stylelint-disable-next-line no-duplicate-selectors */
    ${breakpoints.down('xs')} {
      margin-bottom: 10px;
    }

    img {
      max-width: 100%;
      border: 1px solid lightgray;
      border-radius: 4px;
    }
  `,
  cardTitle: css`
    margin-bottom: 5px;
    ${breakpoints.down('xs')} {
      ${typography2.subtitleMedium}
    }
  `,
  cardSubtitle: css`
    margin-bottom: 20px;
    ${breakpoints.down('xs')} {
      margin-bottom: 10px;
    }
  `,
  cardLabelContainer: css`
    padding: 8px 0;
    ${breakpoints.down('xs')} {
      padding: 0;
      margin-bottom: 10px;
    }
  `,
  cardLabel: css`
    text-transform: uppercase;
  `,
  cardCtaContainer: css`
    margin-left: auto;
    max-width: fit-content;
    ${breakpoints.down('xs')} {
      margin-left: 0;
      max-width: 100%;

      a {
        width: 100%;
      }
    }
  `,
};

type InputProps = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  subtitle: string;
  label: string;
  ctaHref: string;
  ctaLabel: string;
};

type PropsFromIsMobile = {
  isMobile: boolean;
};

type Props = InputProps & PropsFromIsMobile;

export const ArticleEntityCard = ({
  imageAlt,
  imageSrc,
  title,
  ctaHref,
  label,
  subtitle,
  ctaLabel,
  isMobile,
}: Props) => {
  const squareImageDimension = isMobile ? MOBILE_SQUARE_ENTITY_CARD_DIMENSION : DESKTOP_SQUARE_ENTITY_CARD_DIMENSION;
  return (
    <div className="rc-ArticleEntityCard" css={styles.cardContainer}>
      <Grid container>
        <Grid item xs={12} sm={3} css={styles.imageContainer}>
          <div css={styles.imageWrapper}>
            <LazyImgix
              src={imageSrc}
              alt={imageAlt}
              maxWidth={squareImageDimension}
              maxHeight={squareImageDimension}
              imgParams={{ fit: 'scale' }}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography css={styles.cardTitle} component="p" variant="h2semibold">
            {title}
          </Typography>
          <Typography2 css={styles.cardSubtitle} component="p" variant="bodySecondary">
            {subtitle}
          </Typography2>
          <Grid container>
            <Grid item xs={12} sm={6} css={styles.cardLabelContainer}>
              <Typography2 css={styles.cardLabel} component="span" variant="bodySecondary" color="supportText">
                {label}
              </Typography2>
            </Grid>
            <Grid item css={styles.cardCtaContainer} xs={12} sm={6}>
              <Button
                component={TrackedA}
                size="small"
                variant="primary"
                href={ctaHref}
                trackingName="article_entity_card_cta"
                data={{ ctaHref, label }}
                withVisibilityTracking
              >
                {ctaLabel}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default withIsMobile(ArticleEntityCard);
